// App-side delegate for `@heidi/templates/data` (D2.3 · S4, ARCFE-1032).
//
// The classification, the analytics payload shape and the modal choice moved
// into the package. What stays here is the one thing that could not: reading
// the two PostHog flags.
//
// They cross as GETTERS, not values. `IS_NHS_COMP_V2_ENABLED` and
// `IS_TEMPLATE_CREATION_RECORD_ENABLED` are `posthog.isFeatureEnabled` calls,
// which emit `$feature_flag_called` exposure events, and the legacy body only
// reached them inside the compliance branch. Resolving them here at call time
// would fire two extra exposure events on every template save failure —
// aborted requests and generic errors included.
import {
  IS_NHS_COMP_V2_ENABLED,
  IS_TEMPLATE_CREATION_RECORD_ENABLED,
} from '@/features/flags';
import {
  trackNhsTemplateComplianceBlock,
  trackNhsTemplateComplianceWarning,
} from '@/lib/analytics/scribe/compliance';
import { handleTemplateComplianceError as handleTemplateComplianceErrorCore } from '@heidi/templates/data';
import { AxiosError } from 'axios';

type CoreParams = Parameters<typeof handleTemplateComplianceErrorCore>[0];

type DelegateParams = Omit<
  CoreParams,
  'isNhsCompV2Enabled' | 'isTemplateCreationRecordEnabled' | 'dispatch'
> & {
  setRegulatoryGuidelineComplianceReason: (reason: string | null) => void;
};

// The package emits its two compliance events through a narrow port; the emit
// hosts fill it, so callers no longer hand over the Rematch dispatch.
const complianceAnalytics: CoreParams['dispatch'] = {
  analyticsScribe: {
    trackNhsTemplateComplianceWarning,
    trackNhsTemplateComplianceBlock,
  },
};

const getComplianceError = (error: unknown): { reason: string | null } | null => {
  if (!(error instanceof AxiosError)) {
    return null;
  }

  const responseData: unknown = error.response?.data;
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }

  const detail: unknown = Reflect.get(responseData, 'detail');
  if (
    !detail ||
    typeof detail !== 'object' ||
    Reflect.get(detail, 'type') !== 'ComplianceCheckFailedError'
  ) {
    return null;
  }

  const noncompliantInput: unknown = Reflect.get(detail, 'noncompliant_input');
  return {
    reason: typeof noncompliantInput === 'string' ? noncompliantInput : null,
  };
};

// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- UI mutation-error adapter only; template data handling remains in @heidi/templates/data.
export function handleTemplateComplianceError({
  error,
  templateSource,
  setIsRegulatoryGuidelineWarningModalV2Open,
  setRegulatoryGuidelineComplianceReason,
  ...params
}: DelegateParams): boolean {
  const complianceError = getComplianceError(error);
  if (complianceError) {
    setRegulatoryGuidelineComplianceReason(complianceError.reason);
  }

  return handleTemplateComplianceErrorCore({
    ...params,
    error,
    templateSource,
    setIsRegulatoryGuidelineWarningModalV2Open,
    dispatch: complianceAnalytics,
    isNhsCompV2Enabled: IS_NHS_COMP_V2_ENABLED,
    isTemplateCreationRecordEnabled: IS_TEMPLATE_CREATION_RECORD_ENABLED,
  });
}
